<script setup>
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { messageStamp } from 'shared/helpers/timeHelper';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { MODEL_ROUTES_MAP } from '../helpers/constants';

import Button from 'next/button/Button.vue';
import AuditTrailChanges from 'next/wgpt/AuditTrail/AuditTrailChanges.vue';

const props = defineProps({
  record: { type: Object, required: true },
  isExpanded: { type: Boolean, default: false },
});

const emit = defineEmits(['toggle']);

const { t } = useI18n();

const dateText = computed(() => {
  const date = new Date(props.record.date);
  return messageStamp(date.getTime(), 'LLL d, y, h:mm aa');
});

const actionText = computed(() => {
  const action = props.record.action;
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t(`WGPT_AUDIT_TRAIL.COMMON.${action.toUpperCase()}`, action);
});

const modelText = computed(() => {
  const { type, id } = props.record.model;
  const o = useSnakeCase({ [type]: true });
  const snakeCaseModel = Object.keys(o)[0].toUpperCase();
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  const name = t(`WGPT_AUDIT_TRAIL.MODELS.${snakeCaseModel}`, type);
  return `${name} #${id}`;
});

const userText = computed(() => {
  const user = props.record.user;
  if (user) {
    return `${props.record.user.name} (${props.record.user.email})`;
  }

  return t('WGPT_AUDIT_TRAIL.COMMON.SYSTEM_USER');
});

const ipText = computed(() => {
  return props.record.ip || '-';
});

const hasChanges = computed(() => {
  const action = props.record.action;
  if (action === 'sign_in' || action === 'sign_out') {
    return false;
  }
  return true;
});

const modelRoute = computed(() => {
  const { model } = props.record;
  const { type, id } = model;
  const routeData = MODEL_ROUTES_MAP[type];
  if (!routeData) return '';

  const { name, paramKey } = routeData;
  return {
    name,
    params: {
      [paramKey]: id,
    },
  };
});
</script>

<template>
  <div :class="{ collapsed: !isExpanded }">
    <div
      class="grid grid-cols-[1fr_0.5fr_1fr_1fr_0.5fr_auto] gap-4 text-sm items-start"
    >
      <div>
        <div class="text-n-slate-11 row-text">{{ dateText }}</div>
      </div>

      <div>
        <div class="text-n-slate-12 row-text">
          {{ actionText }}
        </div>
      </div>

      <div>
        <div class="text-n-slate-12 row-text">
          <router-link
            v-if="modelRoute"
            :to="modelRoute"
            class="cursor-default pointer-events-none"
            :class="{
              'hover:underline cursor-pointer pointer-events-auto': modelRoute,
            }"
          >
            {{ modelText }}
          </router-link>
          <span v-else>{{ modelText }}</span>
        </div>
      </div>

      <div class="flex items-center gap-2">
        <span class="text-n-slate-12 row-text">
          {{ userText }}
        </span>
      </div>

      <div>
        <div class="text-n-slate-12 row-text">
          {{ ipText }}
        </div>
      </div>

      <div class="min-w-6 flex justify-end">
        <Button
          v-if="hasChanges"
          ghost
          xs
          slate
          icon="i-lucide-chevron-down"
          @click="emit('toggle')"
        />
      </div>
    </div>
    <div
      class="transition-all duration-500 ease-in-out grid overflow-hidden"
      :class="
        isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
      "
    >
      <div class="overflow-hidden">
        <div class="flex flex-col py-8 gap-6">
          <AuditTrailChanges
            :model="modelText"
            :action="record.action"
            :changes="record.changes"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.collapsed .row-text {
  @apply line-clamp-1 break-all;
}
</style>
